import { IconNames } from '@blueprintjs/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { DateFormats, formatDate, openInNewTab } from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { InfoDialog } from '@strata/react-dialog';
import { DetailLabel, DetailLabelValuePair } from '@strata/react-label-value-pairs';

import { EpaCaseDetailFields } from 'components/intakeCases/EpaCaseDetailFields';
import {
    MilestoneTitleDisplayStrings,
    MilestoneType,
    QuestionsAvailableMessage,
} from 'services/constants';
import { handleError } from 'services/Error';
import { getSubmittedAnswers } from 'services/milestoneHelpers';
import { type EPaCase, getAttachmentBlob, useGetAttachmentsQuery } from 'store/api/epaApi';
import { type IntakeCase, type IntakeCaseMilestone } from 'store/api/intakeCaseApi';

interface MilestoneDialogProps {
    milestone: IntakeCaseMilestone;
    onSecondaryButtonClick: () => void;
    epaCases?: EPaCase[];
    intakeCaseDetails?: IntakeCase;
}

export const MilestoneDialog = ({
    milestone,
    onSecondaryButtonClick,
    epaCases = undefined,
    intakeCaseDetails = undefined,
    // eslint-disable-next-line complexity
}: MilestoneDialogProps) => {
    const milestoneType = milestone.title as (typeof MilestoneType)[keyof typeof MilestoneType];
    const isQuestionsAvailableMilestone = milestoneType === MilestoneType.QUESTIONS_AVAILABLE;
    const isQuestionsSubmittedMilestone = milestoneType === MilestoneType.QUESTIONS_SUBMITTED;

    const allResponses = epaCases?.flatMap((epaCaseItem) => epaCaseItem.responses ?? []) ?? [];

    const transactionId = milestone.additional_data?.epa_transaction_id;
    const activeResponse =
        transactionId != null && transactionId !== ''
            ? (allResponses.find((r) => r.transactionId === transactionId) ?? allResponses.at(-1))
            : allResponses.at(-1);

    const { data: attachments } = useGetAttachmentsQuery(
        activeResponse?.transactionId ? { transactionId: activeResponse.transactionId } : skipToken,
    );

    const submittedAnswersMap = isQuestionsSubmittedMilestone
        ? getSubmittedAnswers(activeResponse)
        : [];

    const attachmentTitle =
        milestoneType === MilestoneType.PA_DENIED ? 'Denial letter attached:' : 'Attachment';

    return (
        <InfoDialog
            isOpen={!!milestone}
            onClose={onSecondaryButtonClick}
            width={600}
            dialogTitle={
                MilestoneTitleDisplayStrings[
                    milestone.title as keyof typeof MilestoneTitleDisplayStrings
                ]
            }
            dialogContent={
                <div className="flex__gap--medium" style={{ flexDirection: 'column' }}>
                    <div className="sds_value sds_value--detail">
                        {formatDate(milestone?.timestamp, DateFormats.DATE_TIME)}
                    </div>
                    {isQuestionsAvailableMilestone && <div>{QuestionsAvailableMessage}</div>}
                    <EpaCaseDetailFields
                        milestoneType={milestoneType}
                        activeResponse={activeResponse}
                        intakeCaseDetails={intakeCaseDetails}
                        showErrorResponse
                    />

                    {isQuestionsSubmittedMilestone && submittedAnswersMap.length > 0 && (
                        <div className="flex__gap--medium" style={{ flexDirection: 'column' }}>
                            {submittedAnswersMap.map((response) => (
                                <DetailLabelValuePair
                                    key={response.question}
                                    label={response.question}
                                    value={response.answer}
                                />
                            ))}
                        </div>
                    )}
                    {attachments?.[0] && activeResponse?.transactionId && (
                        <>
                            <DetailLabel label={attachmentTitle} />
                            <ActionButtonText
                                label={
                                    <span style={{ textTransform: 'none' }}>
                                        {attachments[0].attachmentFilename}
                                    </span>
                                }
                                rightIcon={IconNames.DOWNLOAD}
                                onClick={() => {
                                    (async () => {
                                        try {
                                            openInNewTab(
                                                URL.createObjectURL(
                                                    await getAttachmentBlob(
                                                        activeResponse.transactionId,
                                                        attachments[0].attachmentId,
                                                        attachments[0].attachmentFilename,
                                                    ),
                                                ),
                                            );
                                        } catch (err) {
                                            handleError('Error downloading file.', err as Error);
                                        }
                                    })();
                                }}
                            />
                        </>
                    )}
                </div>
            }
        />
    );
};
